import {
  DebtAssociated,
  type User,
  UserSourceOfFunds,
  UserSourceOfWealth,
} from '@generated/graphql';
import type { CustomField } from '@interfaces/customField';
import { Autocomplete } from '@ovComponents/2-component/autocomplete/autocomplete';
import InfoDialog from '@ovComponents/2-component/infoDialog/infoDialog';
import { MenuItem } from '@ovComponents/2-component/menuItem/menuItem';
import { SelectField } from '@ovComponents/2-component/selectField/selectField';
import { TextField } from '@ovComponents/2-component/textField/textField';
import { useCustomFieldRenderer } from '@ovComponents/3-pattern/customField/useCustomFieldRenderer';
import { additionalInfo } from '@ovComponents/4-module/configurableOptionFields/additionalInfo';
import { FieldRequiredType } from '@ovComponents/4-module/configurableOptionFields/basicField';
import { WorkflowCompletionForm } from '@ovComponents/4-module/workflowCompletion/components/workflowCompletionForm';
import { AnalyticsContext } from '@providers/analyticsProvider';
import { ovAnalyticsEvents } from '@utils/analytics/analytics';
import { dollars2cents } from '@utils/dollars2cents';
import { enumValues } from '@utils/enumValues';
import { isCurrencySymbolAfter } from '@utils/moneyFormatting';
import { Box, Grid, Typography } from 'hephaestus/web';
import { kebabCase } from 'lodash-es';
import { useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { useLocalization } from '../../../../../hooks/useLocalization';
import { buildEnrichedCustomOptions, invalidFields } from '../utils';
import {
  shouldEnableDebtAssociated,
  shouldEnableSourceOfWealthAndFunds,
} from './utils';

type SelectOption = {
  label?: string;
  value?: string;
};

export const IncomeAssetsAndDebtsVisual = ({
  options,
  userData,
  loading,
  updateUser,
  continueFunc,
  workflowCompletion,
  grid = false,
  updateMode = false,
  activeCustomFields,
  derivedCustomFields,
  ignoreCustomFieldTriggerRules,
}: {
  options: any;
  userData: Partial<User>;
  loading: boolean;
  grid?: boolean;
  updateMode?: boolean;
  workflowCompletion?: any;
  updateUser: (user: Partial<User>) => void;
  continueFunc: () => void;
  activeCustomFields?: string[];
  derivedCustomFields?: CustomField[];
  ignoreCustomFieldTriggerRules?: boolean;
}) => {
  const { translateBackendString } = useLocalization();
  const { sendAnalytic } = useContext(AnalyticsContext);
  const { t } = useTranslation(['client', 'components']);
  const symbolAfter = isCurrencySymbolAfter();
  const [focused, setFocused] = useState<string[]>([]);
  const [dirty, setDirty] = useState<boolean>(false);
  const [customOptionSelected, setCustomOptionSelected] = useState<any[]>([]);
  const { renderCustomFields, validateAllCustomFields } =
    useCustomFieldRenderer({
      customOptions: customOptionSelected,
      customFieldValues: userData.customFields
        ?.filter((field): field is NonNullable<typeof field> => field !== null)
        ?.map((field) => ({ key: field.key, value: field.value })),
    });

  useEffect(() => {
    setCustomOptionSelected(
      buildEnrichedCustomOptions(
        options,
        activeCustomFields,
        derivedCustomFields,
      ),
    );
  }, [options, activeCustomFields, derivedCustomFields]);

  const sourceOfWealthOptions = [
    {
      value: UserSourceOfWealth.EmploymentIncome,
      label: t('edit.sourceOfWealthOptions.EMPLOYMENT_INCOME'),
    },
    {
      value: UserSourceOfWealth.SaleOfLargeAsset,
      label: t('edit.sourceOfWealthOptions.SALE_OF_LARGE_ASSET'),
    },
    {
      value: UserSourceOfWealth.PropertySale,
      label: t('edit.sourceOfWealthOptions.PROPERTY_SALE'),
    },
    {
      value: UserSourceOfWealth.Inheritance,
      label: t('edit.sourceOfWealthOptions.INHERITANCE'),
    },
    {
      value: UserSourceOfWealth.FamilyEstate,
      label: t('edit.sourceOfWealthOptions.FAMILY_ESTATE'),
    },
    {
      value: UserSourceOfWealth.Investment,
      label: t('edit.sourceOfWealthOptions.INVESTMENT'),
    },
    {
      value: UserSourceOfWealth.BusinessOwnershipInterest,
      label: t('edit.sourceOfWealthOptions.BUSINESS_OWNERSHIP_INTEREST'),
    },
    {
      value: UserSourceOfWealth.Debt,
      label: t('edit.sourceOfWealthOptions.DEBT'),
    },
    {
      value: UserSourceOfWealth.LotteryWinning,
      label: t('edit.sourceOfWealthOptions.LOTTERY_WINNING'),
    },
    {
      value: UserSourceOfWealth.Other,
      label: t('edit.sourceOfWealthOptions.OTHER'),
    },
  ];

  const debtAssociatedOptions = [
    {
      value: DebtAssociated.StudentLoan,
      label: t('edit.debtAssociatedOptions.STUDENT_LOAN'),
    },
    {
      value: DebtAssociated.Mortgage,
      label: t('edit.debtAssociatedOptions.MORTGAGE'),
    },
    {
      value: DebtAssociated.CreditCard,
      label: t('edit.debtAssociatedOptions.CREDIT_CARD'),
    },
  ];

  const sourceOfFundsOptions = enumValues(UserSourceOfFunds).map((item) => ({
    value: item,
    label: t(`edit.sourceOfFundsOptions.${item}`),
  }));

  const cents2dollars = (value?: number | null) =>
    value === undefined || value === null ? '' : value / 100;
  const accreditedInvestorSelectValue =
    { true: 'Yes', false: 'No' }[String(userData.isAccreditedInvestor)] ?? '';
  const enableSourceOfWealthAndFunds = shouldEnableSourceOfWealthAndFunds(
    userData.annualIncomeCents,
    userData.financialLiquidAssetsCents,
    userData.financialFixedAssetsCents,
  );
  const submit = async () => {
    const fields = invalidFields(
      {
        ...options,
        debtAssociated: {
          ...options.debtAssociated,
          requiredIf: (data: any) =>
            data?.totalDebtCents > 0 || data?.annualDebtPaymentsCents > 0,
        },
      },
      {
        ...userData,
        accreditedInvestor: userData.isAccreditedInvestor,
      },
    ).filter((field) => {
      if (!enableSourceOfWealthAndFunds && ['sourceOfWealth'].includes(field)) {
        return false;
      }
      return true;
    });
    const invalidCustomFieldKeys = validateAllCustomFields();
    fields.push(...invalidCustomFieldKeys);
    setFocused(fields);
    if (fields.length === 0) {
      sendAnalytic(
        ovAnalyticsEvents.workflowsIncomeAssetsAndDebtsContinueButtonSelect,
        {
          workflowStepTitle: options?.title,
          workflowId: workflowCompletion?.workflow?.id,
          workflowName: workflowCompletion?.workflow?.name,
          activeWorkflowCompletionId: workflowCompletion?.id,
          objectId: workflowCompletion?.objectId,
          objectType: workflowCompletion?.objectType,
        },
      );
      await continueFunc();
    }
  };

  const update = (newValue: any) => {
    setDirty(true);
    updateUser(newValue);
  };

  useEffect(() => {
    if (loading === false) setDirty(false);
  }, [loading]);

  const lockMessage = (): string =>
    options.customLockedMessage
      ? translateBackendString(options.customLockedMessage)
      : t('pageConfiguration:notEditableMessage');

  const annualIncomeInput = !options?.annualIncomeCents
    ?.enabled ? null : options?.annualIncomeCents?.selectField?.checked ? (
    <Grid size={{ xs: 12, md: grid ? 4 : 12 }}>
      <SelectField
        testId='annual-income'
        disabled={loading}
        label={translateBackendString(options?.annualIncomeCents?.label)}
        infoTooltip={additionalInfo(
          translateBackendString,
          options?.annualIncomeCents?.additionalInfo,
        )}
        fullWidth
        locked={
          options?.annualIncomeCents?.required ===
          FieldRequiredType.NOT_EDITABLE
        }
        lockMessage={lockMessage()}
        onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
          const selectedValue = e.target.value;
          const selectedLabel =
            options?.annualIncomeCents?.selectField?.options?.find(
              (option: SelectOption) => option.value === selectedValue,
            )?.label;
          update({
            ...userData,
            annualIncomeCents: dollars2cents(selectedValue),
            annualIncomeStr: selectedLabel,
          });
        }}
        value={(userData.annualIncomeCents ?? 0) / 100}
        onBlur={() => setFocused([...focused, 'annualIncomeCents'])}
        error={
          userData.annualIncomeCents === undefined &&
          focused.includes('annualIncomeCents') &&
          options?.annualIncomeCents?.required !==
            FieldRequiredType.NOT_REQUIRED
        }
      >
        {options?.annualIncomeCents?.selectField?.options.map(
          (selectOption: SelectOption) => (
            <MenuItem
              data-testid={`annual-income-${kebabCase(selectOption.value)}`}
              value={selectOption.value}
            >
              {selectOption.label}
            </MenuItem>
          ),
        )}
      </SelectField>
    </Grid>
  ) : (
    <Grid size={{ xs: 12, md: grid ? 4 : 12 }}>
      <TextField
        type='dollar'
        testId='annual-income'
        onChange={(e: any) =>
          update({
            ...userData,
            annualIncomeCents: dollars2cents(e.target.value),
          })
        }
        disabled={loading}
        locked={
          options?.annualIncomeCents?.required ===
          FieldRequiredType.NOT_EDITABLE
        }
        lockMessage={lockMessage()}
        label={translateBackendString(options?.annualIncomeCents?.label)}
        infoTooltip={additionalInfo(
          translateBackendString,
          options?.annualIncomeCents?.additionalInfo,
        )}
        fullWidth
        value={cents2dollars(userData.annualIncomeCents)}
        leadingIcon={symbolAfter ? undefined : 'dollar'}
        trailingIcon={symbolAfter ? 'dollar' : undefined}
        onBlur={() => setFocused([...focused, 'annualIncomeCents'])}
        error={
          userData.annualIncomeCents === undefined &&
          focused.includes('annualIncomeCents') &&
          options?.annualIncomeCents?.required !==
            FieldRequiredType.NOT_REQUIRED
        }
      />
    </Grid>
  );

  return (
    <WorkflowCompletionForm
      onSubmit={submit}
      testId='income-assets-and-debts-form'
      showSubmitButton={!updateMode || options.displayUpdateButton}
      submitButtonProps={{
        label: t(updateMode ? 'update' : 'continue'),
        disabled: loading || (updateMode && !dirty),
        dataTestId: `income-assets-${kebabCase(
          translateBackendString(options.title),
        )}-continue-button`,
      }}
    >
      {options?.title && (
        <Typography variant='displayLarge' sx={{ mt: 1, mb: 3 }}>
          {translateBackendString(options?.title)}
          <Box
            sx={{
              display: 'inline-flex',
            }}
          >
            {additionalInfo(
              translateBackendString,
              options?.title?.additionalInfo,
            ) && (
              <InfoDialog
                information={
                  additionalInfo(
                    translateBackendString,
                    options?.title?.additionalInfo,
                  ) ?? ''
                }
              />
            )}
          </Box>
        </Typography>
      )}
      {options?.subtitle && (
        <Typography
          variant='bodyLarge'
          component={'div'}
          sx={{
            mb: 3,
            table: { width: '100%' },
            display: 'flex',
            alignItems: 'end',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              gap: 2,
              flexDirection: 'column',
              m: 0,
              '& > *': { m: 0 },
            }}
          >
            <ReactMarkdown linkTarget='_blank' remarkPlugins={[remarkGfm]}>
              {translateBackendString(options?.subtitle)}
            </ReactMarkdown>
          </Box>
          {additionalInfo(
            translateBackendString,
            options?.subtitle?.additionalInfo,
          ) && (
            <InfoDialog
              information={
                additionalInfo(
                  translateBackendString,
                  options?.subtitle?.additionalInfo,
                ) ?? ''
              }
            />
          )}
        </Typography>
      )}

      <Grid container spacing={2}>
        {annualIncomeInput}
        {options?.financialLiquidAssetsCents?.enabled && (
          <Grid size={{ xs: 12, md: grid ? 4 : 12 }}>
            <TextField
              testId='financial-liquid-assets-cents'
              type='dollar'
              onChange={(e: any) =>
                update({
                  ...userData,
                  financialLiquidAssetsCents: dollars2cents(e.target.value),
                })
              }
              disabled={loading}
              label={translateBackendString(
                options?.financialLiquidAssetsCents?.label,
              )}
              infoTooltip={additionalInfo(
                translateBackendString,
                options?.financialLiquidAssetsCents?.additionalInfo,
              )}
              fullWidth
              locked={
                options?.financialLiquidAssetsCents?.required ===
                FieldRequiredType.NOT_EDITABLE
              }
              lockMessage={lockMessage()}
              value={cents2dollars(userData.financialLiquidAssetsCents)}
              leadingIcon={symbolAfter ? undefined : 'dollar'}
              trailingIcon={symbolAfter ? 'dollar' : undefined}
              errorText={t(
                'client:profile.fieldValidations.liquidAssetsRequired',
              )}
              onBlur={() =>
                setFocused([...focused, 'financialLiquidAssetsCents'])
              }
              error={
                userData.financialLiquidAssetsCents === undefined &&
                focused.includes('financialLiquidAssetsCents') &&
                options?.financialLiquidAssetsCents?.required !==
                  FieldRequiredType.NOT_REQUIRED
              }
            />
          </Grid>
        )}
        {options?.financialFixedAssetsCents?.enabled && (
          <Grid size={{ xs: 12, md: grid ? 4 : 12 }}>
            <TextField
              testId='financial-fixed-assets-cents'
              type='dollar'
              onChange={(e: any) =>
                update({
                  ...userData,
                  financialFixedAssetsCents: dollars2cents(e.target.value),
                })
              }
              disabled={loading}
              locked={
                options?.financialFixedAssetsCents?.required ===
                FieldRequiredType.NOT_EDITABLE
              }
              lockMessage={lockMessage()}
              label={translateBackendString(
                options?.financialFixedAssetsCents?.label,
              )}
              infoTooltip={additionalInfo(
                translateBackendString,
                options?.financialFixedAssetsCents?.additionalInfo,
              )}
              fullWidth
              value={cents2dollars(userData.financialFixedAssetsCents)}
              leadingIcon={symbolAfter ? undefined : 'dollar'}
              trailingIcon={symbolAfter ? 'dollar' : undefined}
              onBlur={() =>
                setFocused([...focused, 'financialFixedAssetsCents'])
              }
              error={
                userData.financialFixedAssetsCents === undefined &&
                focused.includes('financialFixedAssetsCents') &&
                options?.financialFixedAssetsCents?.required !==
                  FieldRequiredType.NOT_REQUIRED
              }
            />
          </Grid>
        )}
        {options?.totalDebtCents?.enabled && (
          <Grid size={{ xs: 12, md: grid ? 4 : 12 }}>
            <TextField
              testId='total-debts-to-cent'
              type='dollar'
              onChange={(e: any) =>
                update({
                  ...userData,
                  totalDebtCents: dollars2cents(e.target.value),
                })
              }
              disabled={loading}
              locked={
                options?.totalDebtCents?.required ===
                FieldRequiredType.NOT_EDITABLE
              }
              lockMessage={lockMessage()}
              label={translateBackendString(options?.totalDebtCents?.label)}
              infoTooltip={additionalInfo(
                translateBackendString,
                options?.totalDebtCents?.additionalInfo,
              )}
              fullWidth
              value={cents2dollars(userData.totalDebtCents)}
              leadingIcon={symbolAfter ? undefined : 'dollar'}
              trailingIcon={symbolAfter ? 'dollar' : undefined}
              onBlur={() => setFocused([...focused, 'totalDebtCents'])}
              error={
                userData.totalDebtCents === undefined &&
                focused.includes('totalDebtCents') &&
                options?.totalDebtCents?.required !==
                  FieldRequiredType.NOT_REQUIRED
              }
            />
          </Grid>
        )}
        {options?.annualDebtPaymentsCents?.enabled && (
          <Grid size={{ xs: 12, md: grid ? 4 : 12 }}>
            <TextField
              testId='annual-debt-payments-cents'
              type='dollar'
              onChange={(e: any) =>
                update({
                  ...userData,
                  annualDebtPaymentsCents: dollars2cents(e.target.value),
                })
              }
              disabled={loading}
              locked={
                options?.annualDebtPaymentsCents?.required ===
                FieldRequiredType.NOT_EDITABLE
              }
              lockMessage={lockMessage()}
              label={translateBackendString(
                options?.annualDebtPaymentsCents?.label,
              )}
              infoTooltip={additionalInfo(
                translateBackendString,
                options?.annualDebtPaymentsCents?.additionalInfo,
              )}
              fullWidth
              value={cents2dollars(userData.annualDebtPaymentsCents)}
              leadingIcon={symbolAfter ? undefined : 'dollar'}
              trailingIcon={symbolAfter ? 'dollar' : undefined}
              onBlur={() => setFocused([...focused, 'annualDebtPaymentsCents'])}
              error={
                userData.annualDebtPaymentsCents === undefined &&
                focused.includes('annualDebtPaymentsCents') &&
                options?.annualDebtPaymentsCents?.required !==
                  FieldRequiredType.NOT_REQUIRED
              }
            />
          </Grid>
        )}
        {options?.debtAssociated?.enabled &&
          shouldEnableDebtAssociated(
            userData.totalDebtCents,
            userData.annualDebtPaymentsCents,
          ) && (
            <Grid size={12}>
              <Autocomplete
                testId='debt-associated'
                multiple
                fullWidth
                locked={
                  options?.debtAssociated?.required ===
                  FieldRequiredType.NOT_EDITABLE
                }
                lockMessage={lockMessage()}
                label={translateBackendString(options?.debtAssociated?.label)}
                infoTooltip={additionalInfo(
                  translateBackendString,
                  options?.debtAssociated?.additionalInfo,
                )}
                onChange={(_e: any, items: any) => {
                  update({
                    ...userData,
                    debtAssociated: [
                      ...items.map(
                        (item: { value: string; label: string }) => item.value,
                      ),
                    ],
                  });
                }}
                value={debtAssociatedOptions.filter(
                  (option) =>
                    userData.debtAssociated &&
                    userData.debtAssociated.includes(option.value),
                )}
                getOptionLabel={(option: { value: string; label: string }) =>
                  option.label
                }
                options={debtAssociatedOptions}
                renderOption={(props: any, option: any) => (
                  <li
                    {...props}
                    data-testid={`debt-associated-option-${kebabCase(
                      option.value,
                    )}`}
                  >
                    {option.label}
                  </li>
                )}
                error={
                  (!userData.debtAssociated ||
                    userData?.debtAssociated?.length === 0) &&
                  focused.includes('debtAssociated') &&
                  options?.debtAssociated?.required !==
                    FieldRequiredType.NOT_REQUIRED
                }
              />
            </Grid>
          )}
        {options?.accreditedInvestor?.enabled && (
          <Grid size={{ xs: 12, md: grid ? 4 : 12 }}>
            <SelectField
              testId='accredited-investor'
              onChange={(e: any) =>
                update({
                  ...userData,
                  isAccreditedInvestor: e.target.value === 'Yes',
                })
              }
              disabled={loading}
              locked={
                options?.accreditedInvestor?.required ===
                FieldRequiredType.NOT_EDITABLE
              }
              lockMessage={lockMessage()}
              label={translateBackendString(options?.accreditedInvestor?.label)}
              infoTooltip={additionalInfo(
                translateBackendString,
                options?.accreditedInvestor?.additionalInfo,
              )}
              fullWidth
              value={accreditedInvestorSelectValue}
              onBlur={() => setFocused([...focused, 'accreditedInvestor'])}
              error={
                typeof userData.isAccreditedInvestor !== 'boolean' &&
                focused.includes('accreditedInvestor') &&
                options?.accreditedInvestor?.required !==
                  FieldRequiredType.NOT_REQUIRED
              }
            >
              <MenuItem data-testid='accredited-investor-yes' value='Yes'>
                {t('components:booleanOptions.yes')}
              </MenuItem>
              <MenuItem data-testid='accredited-investor-no' value='No'>
                {t('components:booleanOptions.no')}
              </MenuItem>
            </SelectField>
          </Grid>
        )}
        {options?.sourceOfWealth?.enabled && enableSourceOfWealthAndFunds && (
          <Grid size={12}>
            <Autocomplete
              testId='source-of-wealth'
              multiple
              fullWidth
              locked={
                options?.sourceOfWealth?.required ===
                FieldRequiredType.NOT_EDITABLE
              }
              lockMessage={lockMessage()}
              label={translateBackendString(options?.sourceOfWealth?.label)}
              infoTooltip={additionalInfo(
                translateBackendString,
                options?.sourceOfWealth?.additionalInfo,
              )}
              onChange={(_e: any, items: any) => {
                update({
                  ...userData,
                  sourceOfWealth: [
                    ...items.map(
                      (item: { value: string; label: string }) => item.value,
                    ),
                  ],
                });
              }}
              value={sourceOfWealthOptions.filter(
                (option) =>
                  userData.sourceOfWealth &&
                  userData.sourceOfWealth.includes(option.value),
              )}
              getOptionLabel={(option: { value: string; label: string }) =>
                option.label
              }
              options={sourceOfWealthOptions}
              renderOption={(props: any, option: any) => (
                <li
                  {...props}
                  data-testid={`source-of-wealth-option-${kebabCase(
                    option.value,
                  )}`}
                >
                  {option.label}
                </li>
              )}
              error={
                (!userData.sourceOfWealth ||
                  userData?.sourceOfWealth?.length === 0) &&
                focused.includes('sourceOfWealth') &&
                options?.sourceOfWealth?.required !==
                  FieldRequiredType.NOT_REQUIRED
              }
            />
          </Grid>
        )}
        {options?.sourceOfFunds?.enabled && enableSourceOfWealthAndFunds && (
          <Grid size={12}>
            <Autocomplete
              testId='source-of-funds'
              multiple
              fullWidth
              label={translateBackendString(options?.sourceOfFunds?.label)}
              locked={
                options?.sourceOfFunds?.required ===
                FieldRequiredType.NOT_EDITABLE
              }
              lockMessage={lockMessage()}
              infoTooltip={additionalInfo(
                translateBackendString,
                options?.sourceOfFunds?.additionalInfo,
              )}
              onChange={(_e: any, items: any) => {
                update({
                  ...userData,
                  sourceOfFunds: [
                    ...items.map(
                      (item: { value: string; label: string }) => item.value,
                    ),
                  ],
                });
              }}
              value={sourceOfFundsOptions.filter(
                (option) =>
                  userData.sourceOfFunds &&
                  userData.sourceOfFunds.includes(option.value),
              )}
              getOptionLabel={(option: { value: string; label: string }) =>
                option.label
              }
              options={sourceOfFundsOptions}
              renderOption={(props: any, option: any) => (
                <li
                  {...props}
                  data-testid={`source-of-funds-option-${kebabCase(
                    option.value,
                  )}`}
                >
                  {option.label}
                </li>
              )}
              error={
                (!userData.sourceOfFunds ||
                  userData?.sourceOfFunds?.length === 0) &&
                focused.includes('sourceOfFunds') &&
                options?.sourceOfFunds?.required !==
                  FieldRequiredType.NOT_REQUIRED
              }
            />
          </Grid>
        )}
        {options?.uniqueCircumstances?.enabled && (
          <Grid size={12}>
            <TextField
              testId='unique-circumstance'
              multiline
              rows={5}
              onChange={(e: any) =>
                update({ ...userData, uniqueCircumstances: e.target.value })
              }
              locked={
                options?.uniqueCircumstances?.required ===
                FieldRequiredType.NOT_EDITABLE
              }
              lockMessage={lockMessage()}
              disabled={loading}
              label={translateBackendString(
                options?.uniqueCircumstances?.label,
              )}
              infoTooltip={additionalInfo(
                translateBackendString,
                options?.uniqueCircumstances?.additionalInfo,
              )}
              fullWidth
              value={userData.uniqueCircumstances}
              onBlur={() => setFocused([...focused, 'uniqueCircumstances'])}
              error={
                userData.uniqueCircumstances === '' &&
                focused.includes('uniqueCircumstances') &&
                options?.uniqueCircumstances?.required !==
                  FieldRequiredType.NOT_REQUIRED
              }
            />
          </Grid>
        )}
        <Grid size={12}>
          {renderCustomFields({
            customData: userData,
            update,
            grid,
            focused,
            setFocused,
            loading,
            ignoreCustomFieldTriggerRules,
            userId: userData.id,
          })}
        </Grid>
      </Grid>
    </WorkflowCompletionForm>
  );
};

export default IncomeAssetsAndDebtsVisual;
